import { isIsoDate, parseIsoDate } from '@ibe/utils';

import { getCurrentLocale, resolveLocale } from './locale.js';

export interface DateFormatterOptions {
  readonly locale?: string;
}

const DEFAULT_OPTIONS: Required<Pick<Intl.DateTimeFormatOptions, 'timeZone'>> = {
  timeZone: 'UTC',
};

// Reference date: 7 Jan 2024 is a Sunday.
const SUNDAY_REFERENCE_YEAR = 2024;
const SUNDAY_REFERENCE_MONTH = 0;
const SUNDAY_REFERENCE_DATE = 7;

// Reference date for bare clock times — 1 Jan 2000, time-of-day only matters.
const TIME_REFERENCE_YEAR = 2000;

function toUtcDate(isoDate: string): Date | null {
  if (!isIsoDate(isoDate)) return null;
  return parseIsoDate(isoDate);
}

function formatDate(
  isoDate: string,
  formatOptions: Intl.DateTimeFormatOptions,
  options: DateFormatterOptions = {},
): string {
  const date = toUtcDate(isoDate);
  if (date === null) return isoDate;
  const locale = options.locale ?? getCurrentLocale();
  return new Intl.DateTimeFormat(resolveLocale(locale), {
    ...DEFAULT_OPTIONS,
    ...formatOptions,
  }).format(date);
}

/** Short display such as "Mon, 6 Jan 25". */
export function formatShortDate(isoDate: string, options?: DateFormatterOptions): string {
  return formatDate(
    isoDate,
    { weekday: 'short', day: 'numeric', month: 'short', year: '2-digit' },
    options,
  );
}

/** Compact display such as "Fri 02 Oct" — weekday, padded day, short month, no year. */
export function formatDayDateMonth(isoDate: string, options?: DateFormatterOptions): string {
  const date = toUtcDate(isoDate);
  if (date === null) return isoDate;
  const locale = options?.locale ?? getCurrentLocale();
  const parts = new Intl.DateTimeFormat(resolveLocale(locale), {
    ...DEFAULT_OPTIONS,
    weekday: 'short',
    day: '2-digit',
    month: 'short',
  }).formatToParts(date);
  const part = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((p) => p.type === type)?.value ?? '';
  return `${part('weekday')} ${part('day')} ${part('month')}`;
}

/** Medium display such as "Mon, 6 Jan 2025". */
export function formatMediumDate(isoDate: string, options?: DateFormatterOptions): string {
  return formatDate(
    isoDate,
    { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' },
    options,
  );
}

/** Long display such as "Monday, 6 January 2025". */
export function formatLongDate(isoDate: string, options?: DateFormatterOptions): string {
  return formatDate(
    isoDate,
    { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' },
    options,
  );
}

/** Month/year heading such as "January 2025". */
export function formatMonthName(
  year: number,
  month: number,
  options?: DateFormatterOptions,
): string {
  const date = new Date(Date.UTC(year, month, 1));
  const locale = options?.locale ?? getCurrentLocale();
  return new Intl.DateTimeFormat(resolveLocale(locale), {
    ...DEFAULT_OPTIONS,
    month: 'long',
    year: 'numeric',
  }).format(date);
}

/** Clock time such as "2:00 PM" from an "HH:mm" or "HH:mm:ss" string. */
export function formatTime(time: string, options?: DateFormatterOptions): string {
  const match = /^(\d{2}):(\d{2})(?::\d{2})?$/.exec(time);
  if (match === null) return time;
  const date = new Date(Date.UTC(TIME_REFERENCE_YEAR, 0, 1, Number(match[1]), Number(match[2])));
  const locale = options?.locale ?? getCurrentLocale();
  return new Intl.DateTimeFormat(resolveLocale(locale), {
    ...DEFAULT_OPTIONS,
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

/** Short day-of-week name such as "Mo", "Di", etc. */
export function formatDayName(weekday: number, options?: DateFormatterOptions): string {
  const date = new Date(
    Date.UTC(SUNDAY_REFERENCE_YEAR, SUNDAY_REFERENCE_MONTH, SUNDAY_REFERENCE_DATE + weekday),
  );
  const locale = options?.locale ?? getCurrentLocale();
  return new Intl.DateTimeFormat(resolveLocale(locale), {
    ...DEFAULT_OPTIONS,
    weekday: 'short',
  }).format(date);
}

/** Formats an ISO-8601 date string into the user's local system date and time. */
export function formatSystemDateTime(isoDate: string): string {
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return isoDate;
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'medium',
  }).format(date);
}

/**
 * Replaces any ISO-8601 UTC timestamp in a message with the user's
 * local system date and time.
 */
export function formatErrorWithSystemTime(message: string): string {
  return message.replace(/\b\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z?\b/g, (match) =>
    formatSystemDateTime(match),
  );
}
